// src/store/modules/socketStore.js
import io from "socket.io-client";
import { eventBus } from "@/eventBus.js";

const state = {
  socket: null,
  socketSession: null,
  isConnected: false,
  isConnecting: false,
  inQueue: false,
  inAttendance: false,
  attendance: null,
  messages: [],
  lastUpdatedQueue: null,
  code: null,
  audience: null,
  audienceMessages: [],
  entityConversations: [],
  shouldClearStorage: false,
  appointment: null,
};

// Converte a mensagem crua (socket ou API) para o formato usado pelo chat.
function mapMessage(attendance, data) {
  const { author, createdAt, text, _id } = data;
  const authorID = author?._id;
  const isRequester = !authorID || authorID === attendance?.requester?._id;

  return {
    _id,
    me: isRequester,
    createdAt,
    author: {
      name: author?.name,
      image: "",
    },
    message: text,
  };
}

const mutations = {
  SET_SOCKET(state, socket) {
    state.socket = socket;
  },
  SET_SOCKET_SESSION(state, data) {
    state.socketSession = data;
  },
  SET_CONNECTED(state, status) {
    state.isConnected = status;
  },
  SET_CONNECTING(state, value) {
    state.isConnecting = value;
  },
  SET_IN_QUEUE(state, status) {
    state.inQueue = status;
  },
  SET_IN_ATTENDANCE(state, status) {
    state.inAttendance = status;
    state.inQueue = false;
  },
  SET_ATTENDANCE(state, attendance) {
    state.attendance = attendance;
  },
  SET_MESSAGES(state, messages) {
    state.messages = messages;
  },
  ADD_MESSAGE(state, data) {
    if (state.messages.find((m) => m._id === data._id)) return;

    state.messages.push(mapMessage(state.attendance, data));
  },
  // Substitui o histórico inteiro de uma vez. Usado ao reconectar: é o que
  // traz de volta as mensagens que chegaram enquanto o socket estava fora.
  SYNC_MESSAGES(state, messages) {
    state.messages = (messages || []).map((message) =>
      mapMessage(state.attendance, message)
    );
  },
  ADD_AUDIENCE_MESSAGE(state, data) {
    if (state.audienceMessages.find((el) => el._id === data._id)) return;
    state.audienceMessages.push(data);
  },
  SET_AUDIENCE_MESSAGES(state, data) {
    state.audienceMessages = data;
  },
  SET_AUDIENCE(state, data) {
    state.audience = data;
  },
  CLEAR_MESSAGES(state) {
    state.messages = [];
  },
  FINISH_ATTENDANCE(state) {
    state.attendance = null;
    state.inAttendance = false;
    state.inQueue = false;
    state.lastUpdatedQueue = null;
    state.messages = [];
  },
  TRANSFER_ATTENDANCE(state, data) {
    state.attendance = data;
    state.inAttendance = false;
    state.inQueue = true;
    state.lastUpdatedQueue = null;
  },
  LAST_UPDATED_QUEUE(state, data) {
    if (state.attendance?.desk?._id === data.deskID) {
      state.lastUpdatedQueue = new Date().getTime();
    }
  },
  SET_CODE(state, data) {
    state.code = data;
  },
  RESET_ATTENDANCE(state) {
    state.inQueue = false;
    state.inAttendance = false;
    state.attendance = null;
    state.messages = [];
    state.lastUpdatedQueue = null;
    state.code = null;
    state.appointment = null;
  },
  RESET_AUDIENCE(state) {
    state.audience = null;
    state.audienceMessages = [];
  },
  SET_ENTITY_CONVERSATIONS(state, list) {
    state.entityConversations = (list || []).map((c) => ({
      with: c.with,
      lastMessage: c.lastMessage,
      lastAt: c.lastAt,
    }));
  },
  UPSERT_ENTITY_CONVERSATION(state, msg) {
    // No lado do cidadão, a "conversa" é com o admin
    const adminUserID = msg?.conversation?.adminUserID;
    if (!adminUserID) return;

    const existing = state.entityConversations.find(
      (c) => c.with.userID === adminUserID
    );

    if (existing) {
      existing.lastMessage = msg.message;
      existing.lastAt = msg.createdAt;
      if (msg.admin?.person) {
        existing.with.person = { ...existing.with.person, ...msg.admin.person };
      }
    } else {
      state.entityConversations.unshift({
        with: { userID: adminUserID, person: msg.admin?.person || {} },
        lastMessage: msg.message,
        lastAt: msg.createdAt,
      });
    }
  },
  RESET_ENTITY_CONVERSATIONS(state) {
    state.entityConversations = [];
  },
  SET_SHOULD_CLEAR_STORAGE(state, value) {
    state.shouldClearStorage = value;
  },
  SET_APPOINTMENT(state, data) {
    state.appointment = data;
  },
  UPDATE_APPOINTMENT(state, data) {
    if (state.appointment) {
      state.appointment = { ...state.appointment, ...data };
    } else {
      state.appointment = data;
    }
  },
  CLEAR_APPOINTMENT(state) {
    state.appointment = null;
  },
};

let visibilityHandlerRegistered = false;

// No mobile, minimizar o browser congela a aba e derruba o socket. Ao voltar,
// nem sempre o socket.io percebe a queda (ou já esgotou reconnectionAttempts),
// então forçamos a reconexão/resync quando a aba volta a ficar visível.
function registerVisibilityResync(dispatch) {
  if (visibilityHandlerRegistered || typeof document === "undefined") return;
  visibilityHandlerRegistered = true;

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") dispatch("resyncAttendance");
  });
}

async function emitWithReconnectCheck(store, event, payload, callback) {
  // Garante que exista UMA instância de socket e que ela esteja conectando.
  // connectSocket é idempotente: reaproveita o socket existente em vez de
  // criar outro — era a criação repetida que gerava a tempestade de conexões.
  if (!store.state.socket) {
    await store.dispatch("connectSocket");
  } else if (!store.state.isConnected) {
    store.state.socket.connect();
  }

  const { socket } = store.state;
  if (!socket) {
    return callback?.({ message: "Socket não inicializado" });
  }

  // socket.io enfileira o emit enquanto desconectado e o envia ao reconectar.
  socket.emit(event, payload, (...args) => {
    if (typeof callback === "function") callback(...args);
  });
}

const actions = {
  connectSocket(store) {
    const { state, commit, dispatch } = store;

    registerVisibilityResync(dispatch);

    // Socket já existe: reaproveita a MESMA instância. Nunca cria um segundo
    // socket — quem reconecta é o próprio socket.io.
    if (state.socket) {
      if (!state.isConnected) state.socket.connect();
      return Promise.resolve();
    }

    commit("SET_CONNECTING", true);

    const now = Date.now();
    const ttl = 3600 * 1000 * 8;
    const isExpirated = (state.socketSession?.connectedAt || 0) + ttl < now;

    const socket = io(process.env.VUE_APP_SOCKET_URL, {
      autoConnect: false,
      reconnectionAttempts: 10,
      reconnectionDelayMax: 5000,
      auth: { xApiKey: process.env.VUE_APP_API_KEY },
      ...(state.socketSession?.sessionID &&
        !isExpirated && {
          query: { sessionID: state.socketSession?.sessionID },
        }),
    });

    commit("SET_SOCKET", socket);

    // Listeners registrados UMA única vez, aqui na criação do socket.
    // Antes ficavam dentro do handler de "connect" e eram re-registrados
    // (duplicados) a cada reconexão.
    socket.on("connect", () => {
      console.log("Socket connected");
      commit("SET_CONNECTED", true);
      commit("SET_CONNECTING", false);

      if (state.attendance?.protocol) {
        socket.emit("attendance:reconnect", {
          protocol: state.attendance?.protocol,
        });
      }

      if (state.appointment?.protocol) {
        socket.emit("appointments:reconnect", {
          appointmentID: state.appointment._id,
          protocol: state.appointment.protocol,
        });
      }
    });

    socket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);

      if (state.appointment?._id && reason !== "io client disconnect") {
        eventBus.$emit("socket:disconnected-appointment", {
          appointmentID: state.appointment._id,
        });
      }

      commit("SET_CONNECTED", false);
      commit("SET_CONNECTING", false);
      // Não zera state.socket: a mesma instância reconecta sozinha.
      // Zerar fazia o app criar um socket novo a cada ping/emit — era
      // a causa raiz da tempestade de conexões.
    });

    socket.on("socket:connected", (data) => {
      commit("SET_SOCKET_SESSION", data);
    });

    socket.on("entities:queue:reconnected", (data) => {
      eventBus.$emit("entities:queue:reconnected", data);
    });

    socket.on("audiences:chat:reconnected", (data) => {
      eventBus.$emit("audiences:chat:reconnected", data);
    });

    socket.on("attendance:started", (data) => {
      commit("SET_ATTENDANCE", data);
      commit("SET_IN_ATTENDANCE", true);
      commit("SET_IN_QUEUE", false);
    });

    socket.on("attendance-messages", (data) => {
      commit("ADD_MESSAGE", data);
    });

    socket.on("attendance:finished", (data) => {
      commit("FINISH_ATTENDANCE");
      commit("SET_CODE", data?.code);
      eventBus.$emit("attendance:finished", data);
    });

    socket.on("attendance:canceled", () => {
      commit("FINISH_ATTENDANCE");
    });

    socket.on("attendance:queue", (data) => {
      commit("LAST_UPDATED_QUEUE", data);
    });

    socket.on("attendance:transferred", (data) => {
      commit("TRANSFER_ATTENDANCE", data);
    });

    socket.on("appointments:created", (data) => {
      eventBus.$emit("appointments:created", data);
    });

    socket.on("appointments:started", (data) => {
      if (data?.appointment) {
        commit("UPDATE_APPOINTMENT", data.appointment);
      }
      eventBus.$emit("appointments:started", data);
    });

    socket.on("appointments:in_progress", (data) => {
      if (data) {
        commit("UPDATE_APPOINTMENT", data);
      }
      eventBus.$emit("appointments:in_progress", data);
    });

    socket.on("appointments:canceled", (data) => {
      eventBus.$emit("appointments:canceled", data);
    });

    socket.on("appointments:finished", (data) => {
      eventBus.$emit("appointments:finished", data);
    });

    socket.on("appointments:no_show", (data) => {
      eventBus.$emit("appointments:no_show", data);
    });

    socket.on("appointments:updated", (data) => {
      if (data?.appointment) {
        commit("UPDATE_APPOINTMENT", data.appointment);
      }
      eventBus.$emit("appointments:updated", data);
    });

    socket.on("appointments:entered", (data) => {
      if (data?.appointment) {
        commit("UPDATE_APPOINTMENT", data.appointment);
      }
      eventBus.$emit("appointments:entered", data);
    });

    socket.on("appointments:enter", (data) => {
      eventBus.$emit("appointments:enter", data);
    });

    socket.on("appointments:leave", (data) => {
      eventBus.$emit("appointments:leave", data);
    });

    socket.on("appointments:ready", (data) => {
      commit("UPDATE_APPOINTMENT", {
        showMeetLink: true,
        meetingLink: data.meetingLink,
        isReady: data.isReady,
      });
      eventBus.$emit("appointments:ready", data);
    });

    socket.on("appointments:client:ready", (data) => {
      commit("UPDATE_APPOINTMENT", { isReady: data.isReady });
      eventBus.$emit("appointments:client:ready", data);
    });

    socket.on("appointments:meeting:joined", (data) => {
      console.log("Socket appointments:meeting:joined received:", data);
      eventBus.$emit("appointments:meeting:joined", data);
    });

    socket.on("appointments:meeting:left", (data) => {
      eventBus.$emit("appointments:meeting:left", data);
    });

    socket.on("appointments:transferred", (data) => {
      commit("SET_ATTENDANCE", null);
      commit("SET_IN_ATTENDANCE", false);
      commit("SET_IN_QUEUE", false);
      if (data?.appointment) {
        commit("UPDATE_APPOINTMENT", data.appointment);
      }
      eventBus.$emit("appointments:transferred", data);
    });

    socket.on("attendance:reconnected", (data) => {
      commit("SET_ATTENDANCE", data);
      commit("LAST_UPDATED_QUEUE", { deskID: data?.desk?._id });

      // O servidor devolve o histórico completo em data.messages. Trocar o
      // array inteiro recupera o que chegou enquanto o app estava fora —
      // no mobile o browser minimizado derruba o socket.
      if (data?.messages) {
        commit("SYNC_MESSAGES", data.messages);
      }
    });

    socket.on("appointments:reconnected", (data) => {
      if (data?.appointment) {
        commit("UPDATE_APPOINTMENT", data.appointment);
      }
      eventBus.$emit("appointments:reconnected", data);
    });

    socket.on("pong", () => {
      console.log("pong");
      // Keep-alive
    });

    socket.on("audiences:chat:receive", (data) => {
      commit("ADD_AUDIENCE_MESSAGE", data);
    });

    socket.on("audiences:chat:meet-enabled", (data) => {
      if (state.socketSession?.clientID !== data.clientID) return;
      commit("SET_AUDIENCE", { ...state.audience, meet: data.meet });
    });

    socket.on("audiences:chat:added", (data) => {
      eventBus.$emit("audiences:chat:added", data);
    });

    socket.on("audiences:chat:removed", (data) => {
      eventBus.$emit("audiences:chat:removed", data);
    });

    socket.on("audiences:chat:requeued", (data) => {
      eventBus.$emit("audiences:chat:requeued", data);
    });

    socket.on("audiences:chat:finished", (data) => {
      eventBus.$emit("audiences:chat:finished", data);
    });

    socket.on("entities:queue:updated", (data) => {
      eventBus.$emit("entities:queue-updated", data);
    });

    socket.on("entities:queue:removed", (data) => {
      eventBus.$emit("entities:queue:removed", data);
    });

    socket.on("entities:private:receive", (data) => {
      commit("UPSERT_ENTITY_CONVERSATION", data);
      eventBus.$emit("entities:private:receive", data);
    });

    socket.on("attendance:time-out", (data) => {
      eventBus.$emit("attendance:time-out", data);
    });

    socket.connect();

    return Promise.resolve();
  },

  disconnectSocket({ state, commit, dispatch }) {
    if (state.socket) {
      if (state.appointment?._id) {
        dispatch("markAppointmentNotReady", {
          appointmentID: state.appointment._id,
        });
      }

      state.socket.removeAllListeners();
      state.socket.disconnect();
      commit("SET_CONNECTED", false);
      commit("SET_SOCKET", null);
    }
  },

  async createNewAttendance(store, { callback }) {
    return emitWithReconnectCheck(store, "attendance:new", {}, callback);
  },

  async requestAttendance(store, { data, callback }) {
    return emitWithReconnectCheck(store, "attendance:request", data, callback);
  },

  async cancelAttendance(store, { data, callback }) {
    return emitWithReconnectCheck(store, "attendance:cancel", data, callback);
  },

  async joinAttendance(store, { data }) {
    return emitWithReconnectCheck(store, "attendance:join", data);
  },

  async sendMessage(store, { data, callback }) {
    return emitWithReconnectCheck(
      store,
      "attendance-messages:new",
      data,
      callback
    );
  },

  async finishAttendance(store, { data, callback }) {
    return emitWithReconnectCheck(store, "attendance:finish", data, callback);
  },

  async inactivateAttendance(store, { data }) {
    return emitWithReconnectCheck(store, "attendance:inactive", data);
  },

  async dialogAttendance(store, { data, callback }) {
    return emitWithReconnectCheck(store, "attendance:dialog", data, callback);
  },

  async pingEmit(store) {
    return emitWithReconnectCheck(store, "ping");
  },

  async joinEntitiesQueue(store, { data, callback }) {
    return emitWithReconnectCheck(store, "entities:queue:join", data, callback);
  },

  async leaveEntitiesQueue(store, { data, callback }) {
    return emitWithReconnectCheck(
      store,
      "entities:queue:leave",
      data,
      callback
    );
  },

  async removeChatAudience(store, { data, callback }) {
    return emitWithReconnectCheck(
      store,
      "audiences:chat:remove",
      data,
      callback
    );
  },

  async sendChatAudience(store, { data }) {
    return emitWithReconnectCheck(store, "audiences:chat:send", data);
  },

  async audiencesChatJoin(store, { data, callback }) {
    return emitWithReconnectCheck(store, "audiences:chat:join", data, callback);
  },

  async audiencesChatLeave(store, { data, callback }) {
    return emitWithReconnectCheck(
      store,
      "audiences:chat:leave",
      data,
      callback
    );
  },

  async getEntityPrivateConversations(store, { data, callback }) {
    return emitWithReconnectCheck(
      store,
      "entities:private:conversations",
      data,
      callback
    );
  },

  async getEntityPrivateHistory(store, { data, callback }) {
    return emitWithReconnectCheck(
      store,
      "entities:private:history",
      data,
      callback
    );
  },

  async sendEntityPrivateMessage(store, { data, callback }) {
    return emitWithReconnectCheck(
      store,
      "entities:private:send",
      data,
      callback
    );
  },

  setEntityConversations({ commit }, data) {
    commit("SET_ENTITY_CONVERSATIONS", data);
  },

  resetEntityConversations({ commit }) {
    commit("RESET_ENTITY_CONVERSATIONS");
  },

  async markAppointmentNotReady(store, { appointmentID }) {
    const { state } = store;
    if (state.socket && state.isConnected) {
      state.socket.emit("appointments:mark-not-ready", { appointmentID });
    }
  },

  async registerAppointmentSocket(store, { appointmentID }) {
    // emitWithReconnectCheck garante o socket e o socket.io enfileira o emit
    // se ainda não conectou — sem depender de isConnected síncrono.
    return emitWithReconnectCheck(store, "appointments:register-socket", {
      appointmentID,
    });
  },
  async unregisterAppointmentSocket(store, { appointmentID }) {
    const { state } = store;
    if (state.socket && state.isConnected) {
      state.socket.emit("appointments:unregister-socket", { appointmentID });
    }
  },

  async enterAppointment(store, { data, callback }) {
    return emitWithReconnectCheck(store, "appointments:enter", data, callback);
  },

  async reconnectAppointment(store, { data, callback }) {
    return emitWithReconnectCheck(
      store,
      "appointments:reconnect",
      data,
      callback
    );
  },

  async leaveAppointment(store, { data, callback }) {
    return emitWithReconnectCheck(store, "appointments:leave", data, callback);
  },

  async setAppointmentReady(store, { data, callback }) {
    return emitWithReconnectCheck(store, "appointments:ready", data, callback);
  },

  async joinAppointmentMeeting(store, { data }) {
    return emitWithReconnectCheck(store, "appointments:meeting:join", data);
  },

  async enterAppointmentMeet(store, { data, callback }) {
    return emitWithReconnectCheck(
      store,
      "appointments:meeting:enter",
      data,
      callback
    );
  },

  async leaveAppointmentMeeting(store, { data, callback }) {
    return emitWithReconnectCheck(
      store,
      "appointments:meeting:leave",
      data,
      callback
    );
  },

  // Reconecta (se preciso) e pede ao servidor o histórico completo do
  // atendimento. Chamado ao voltar do background no mobile.
  resyncAttendance({ state }) {
    const { socket, attendance } = state;
    if (!socket || !attendance?.protocol) return;

    if (!socket.connected) {
      // connect() também zera o contador de reconnectionAttempts, que pode
      // ter estourado enquanto a aba estava congelada. O handler de "connect"
      // já dispara o attendance:reconnect.
      socket.connect();
      return;
    }

    socket.emit("attendance:reconnect", { protocol: attendance.protocol });
  },

  syncMessages({ commit }, data) {
    commit("SYNC_MESSAGES", data);
  },

  async setMessages({ commit }, data) {
    commit("SET_MESSAGES", data);
  },
  async setAttendance({ commit }, data) {
    commit("SET_ATTENDANCE", data);
  },
  setInQueue({ commit }, data) {
    commit("SET_IN_QUEUE", data);
  },
  setCode({ commit }, data) {
    commit("SET_CODE", data);
  },
  addMessage({ commit }, data) {
    commit("ADD_MESSAGE", data);
  },
  addAudienceMessage({ commit }, data) {
    commit("ADD_AUDIENCE_MESSAGE", data);
  },
  setAudienceMessage({ commit }, data) {
    commit("SET_AUDIENCE_MESSAGES", data);
  },
  setAudience({ commit }, data) {
    commit("SET_AUDIENCE", data);
  },
  resetAttendance({ commit }) {
    commit("RESET_ATTENDANCE");
  },
  resetAudience({ commit }) {
    commit("RESET_AUDIENCE");
  },
  setShouldClearStorage({ commit }, value) {
    commit("SET_SHOULD_CLEAR_STORAGE", value);
  },
  setAppointment({ commit }, data) {
    commit("SET_APPOINTMENT", data);
  },
  updateAppointment({ commit }, data) {
    commit("UPDATE_APPOINTMENT", data);
  },
  clearAppointment({ commit }) {
    commit("CLEAR_APPOINTMENT");
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters: {},
};
